import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// selectors
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// components
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { EditedTaxFieldInputContainer } from './EditedTaxField.style';

// actions
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import type { RetailPriceFieldProps } from '../types';
import { getTaxFieldName, useEditableProp } from './helpers';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { DealTabs } from '@makemydeal/dr-shared-types';

// consts/enums
import { TAXES_RATE_MAX_VALUE_MESSAGE } from '../constants';

export default function TaxItemRateField<Props extends RetailPriceFieldProps>({ data, handleBlur, maxValue, label }: Props) {
    const [rate, setRate, isEdited] = useEditableProp(data.rate, data.isEditedRate);
    const fieldName = getTaxFieldName(data.name, true);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isTaxRateValid = useSelector(dealScreenErrorSelectors.getFieldValidationError(DealTabs.TAXES, fieldName));
    const isLocked = dealLockHooks.useIsDealLocked();
    const dispatch = useDispatch();
    const prevValueBeforeBlur = useRef<string>(data.rate || '');
    const isBlurInProgress = useRef(false);
    const isTaxRateFieldRef = useRef(isTaxRateValid);

    const handleBlurWrapper = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        isBlurInProgress.current = true;
        if (!rate && !data.rate) {
            setRate('0');
            prevValueBeforeBlur.current = '0';
            return;
        }
        prevValueBeforeBlur.current = e.target.value;
        handleBlur(e, data.key);
    };

    useEffect(() => {
        isTaxRateFieldRef.current = isTaxRateValid;
    }, [isTaxRateValid]);

    useEffect(() => {
        return () => {
            if (isTaxRateFieldRef.current) {
                dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, false));
            }
        };
    }, []);

    useEffect(() => {
        if (!data.hasError) {
            setRate(data.rate);
            prevValueBeforeBlur.current = data.rate;
        }

        if (isDealScreenRedDotErrorsEnabled && isTaxRateFieldRef.current !== data.hasError) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, data.hasError));
        }
    }, [data.hasError, data.rate, setRate]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleBlurWrapper({ target: { value: e.currentTarget.value } });
            prevValueBeforeBlur.current = e.currentTarget.value;
        }
    };

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        if (isBlurInProgress.current) {
            isBlurInProgress.current = false;
            return;
        }
        dispatch(offerActionCreators.updatePaymentCallPendingState(e.target.value !== String(prevValueBeforeBlur.current)));

        const hasErrorInRateField = !e.isValid;
        if (isDealScreenRedDotErrorsEnabled && isTaxRateValid !== hasErrorInRateField)
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, hasErrorInRateField));

        setRate(e.target.value);
    };

    return (
        <EditedTaxFieldInputContainer $isEdited={isEdited}>
            <NumericInput
                data-testid={`detailed-table-cell-input-${data.name}`}
                name={'detail-rate'}
                inputSuffix={'%'}
                sx={{ width: '9rem' }}
                onChange={handleChange}
                value={rate}
                onBlur={handleBlurWrapper}
                onKeyDown={handleKeyDown}
                allowDecimal={true}
                decimalMaxLength={5}
                maxValue={maxValue || 99.99999}
                placeholder={'0'}
                autoInsertCommas={true}
                hasError={data.hasError}
                label={label || ''}
                disabled={isLocked || data.isDisabled || false}
                size="sm"
                customErrorMessages={{
                    exceedsMaxValueRule: TAXES_RATE_MAX_VALUE_MESSAGE
                }}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </EditedTaxFieldInputContainer>
    );
}
